import React, { useState, useEffect, useCallback } from 'react';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import {
    Grid,
    Card,
    Typography,
    FormControlLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    CardContent,
    Toolbar,
    Radio,
    RadioGroup,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import TextField from '../../components/text_field';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import Moment from 'react-moment';
import DownloadButton from '../../components/download_button';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/agreements.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

const StyledCardContent = styled(CardContent)({
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
        marginBottom: '0.5em'
    }
});

type UserProfileProps = {
    setTitle?: (title: string) => void;
};

type UserProfileData = {
    FirstName?: string;
    LastName?: string;
    Title?: string;
    Email?: string;
    Phone?: string;
    [key: string]: any;
};

const UserProfile = (props: UserProfileProps) => {
    const { setTitle } = props;
    const [profile, setProfile] = useState<UserProfileData>({});
    const [errorDialog, setErrorDialog] = useState(false);
    const [verificationDialog, setVerificationDialog] = useState(false);
    const [readyForCode, setReadyForCode] = useState(false);
    const [deliveryOption, setDeliveryOption] = useState("sms");
    const [error, setError] = useState<string | undefined>(undefined);

    const loadProfile = useCallback(async () => {
        var profile = (await axios.get(Config.oauth + "/user/userprofile")).data;
        setProfile(profile);
    }, []);

    useEffect(() => {
        if (setTitle) {
            setTitle('User Profile');
        }
        loadProfile();
    }, [setTitle, loadProfile]);

    const showErrorDialog = (ret: any) => {
        var msg = "Unexpected error.";
        if (ret.messages) {
            msg = "";
            for (var i = 0; i < ret.messages.length; i++) {
                msg += ret.messages[i] + "\n";
            }
        }
        setErrorDialog(true);
        setError(msg);
    }

    const onSubmit = async (e: any) => {
        if(!verificationDialog){
            setReadyForCode(false);
            e.DeliveryOption = "unknown";
        }else{
            e.DeliveryOption = deliveryOption;
        }
        var ret = (await axios.put(Config.oauth + "/user/userprofile", e)).data;
        if(ret.success){
            setVerificationDialog(false);
            await loadProfile();
        }else if(ret.verificationCodeRequired && !verificationDialog){
            setVerificationDialog(true);
        // Pre-existing bug (predates this migration, confirmed identical at
        // ee28306a): this read `this.readyForCode` (an instance property
        // that's never declared -- only `this.state.readyForCode` exists),
        // which TS correctly rejects with "Property 'readyForCode' does not
        // exist on type 'UserProfile'". Since that expression was always
        // `undefined` at runtime, `!this.readyForCode` was always `true`, so
        // this branch fired on every submit where the server still reports
        // verificationCodeRequired -- including a *wrong* verification code
        // -- instead of falling through to showErrorDialog(ret). Net effect:
        // entering an incorrect verification code silently re-showed the
        // code field instead of telling the user the code was wrong. Fixed
        // by reading the intended `readyForCode` state.
        }else if(ret.verificationCodeRequired && !readyForCode){
            setReadyForCode(true);
        }else{
            showErrorDialog(ret);
        }
    }

    const cancel = () => {
        setVerificationDialog(false);
    }

    var config = {
        getData: async function(){
            var data = (await axios.get(Config.oauth + '/user/loginhistory')).data.Data;
            return data;
        },
        columns: [
          { id: "Id", hidden: true },
          { id: 'Address', filterable: true, sortable: true, label: 'IP Address', width: '15em' },
          { id: 'LastLogin', type: 'date', sortable: true, label: 'Last Login', template: (value: any) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>) }
      ],
      order: 'desc',
      orderBy: 'LastLogin',
      keyField: 'Id',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      title: 'Login History'
    };
    return (<Grid container spacing={2}>
        <Grid size={{ md: 12 }} style={{display: 'flex'}}>
            <Card style={{flex: 1}}>
                <Toolbar className='lbtoolbar'>Your Information</Toolbar>
                <StyledCardContent>
                    <Form onSubmit={onSubmit} initialValues={profile} render={({ handleSubmit, pristine, invalid }: any) => {
                          return (
                              <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                              <Field style={{marginBottom: '.5em'}} component={TextField} label='First Name' name='FirstName'/>
                              <Field style={{marginBottom: '.5em'}} component={TextField} label='Last Name' name='LastName'/>
                              <Field style={{marginBottom: '.5em'}} component={TextField} label='Title' name='Title'/>
                              <Field style={{marginBottom: '.5em'}} component={TextField} label='Email' name='Email'/>
                              <Field style={{marginBottom: '.5em'}} component={TextField} label='Phone' name='Phone'/>
                               <div style={{textAlign: 'left'}}>
                                <DownloadButton onClick={handleSubmit} disabled={pristine} variant='outlined' color='primary' style={{marginRight: '1em'}}>Save</DownloadButton>
                                <Button variant='outlined' href={Config.oauth + '/manage/Account/ForgotPassword'}>Reset Password</Button>
                               </div>
                               {/* Pre-existing bug (predates this migration, confirmed identical
                                   at ee28306a): onClose set `verificationDialogDialog` (an
                                   extra, never-declared "Dialog" typo) instead of
                                   `verificationDialog`, so TS correctly rejects the object
                                   literal against UserProfileState. At runtime this meant
                                   dismissing the dialog via backdrop-click/Escape silently did
                                   nothing -- only the "Cancel" button (which calls cancel,
                                   setting the correctly-spelled key) actually closed it. Fixed
                                   by correcting the key so onClose behaves like Cancel. */}
                               <Dialog open={verificationDialog} onClose={() => {setVerificationDialog(false)}}>
                                   <Toolbar className='lbtoolbar'>Verification Required</Toolbar>
                                   <DialogContent style={{display: 'flex', flexDirection: 'column'}}>
                                   {readyForCode ?
                                   <>
                                    <Typography>{'Please enter your verification code to continue.'}</Typography>
                                    <Field validate={(value: any) => value ? undefined : 'Required'} style={{marginBottom: '.5em', marginTop: '.5em'}} component={TextField} label='Verificaton Code' name='PhoneCode'/>
                                   </>
                                   : <>
                                    <Typography>{'Please choose how you would like to receive your verification code.'}</Typography>
                                    <RadioGroup value={deliveryOption} onChange={(e: any) => setDeliveryOption(e.target.value)}>
                                        <FormControlLabel value="sms" control={<Radio />} label="Text message"/>
                                        <FormControlLabel value="phone" control={<Radio />} label="Phone call"/>
                                    </RadioGroup>
                                    </>
                                   }
                                   </DialogContent>
                                   <DialogActions>
                                    <Button variant='text' onClick={cancel}>Cancel</Button>
                                    <DownloadButton variant='text' onClick={handleSubmit} disabled={pristine || invalid} color='primary'>Continue</DownloadButton>
                                   </DialogActions>
                               </Dialog>
                              </form>
                          );}}/>
                    </StyledCardContent>
            </Card>
        </Grid>
        <Grid size={{ md: 12 }}>
            <EnhancedTable config={config}/>
        </Grid>
        <Dialog open={errorDialog} onClose={() => {setErrorDialog(false)}}>
            <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
            <DialogContent><Typography>{error}</Typography></DialogContent>
        </Dialog>
        </Grid>);
}

export default UserProfile;
